import type {
  ArtifactPlan,
  BobState,
  MergePlan,
  PromptForgeResult,
  RepoGraderResult,
  SovereignRuntimeStatus,
} from "../../types";
import { getDefaultAdapters, refreshAdapterAvailability } from "../adapters";

export function buildSovereignStatus(adapters = getDefaultAdapters()): SovereignRuntimeStatus {
  const refreshed = refreshAdapterAvailability(adapters);
  return {
    offlineCapable: true,
    apiKeysRequired: false,
    storageMode: "localStorage",
    exportMode: "browser-download",
    adapters: refreshed,
    noPaywallLocked: true,
    checkedAt: new Date().toISOString(),
  };
}

export function exportBuildPlanMd(state: BobState): string {
  const latestPlan = state.artifactPlans[state.artifactPlans.length - 1];
  const latestIntake = state.intakes[state.intakes.length - 1];
  let md = `# BOB Build Plan Export\n\n`;
  if (latestIntake) {
    md += `## Intake: ${latestIntake.projectName}\n`;
    md += `- Type: ${latestIntake.projectType}\n`;
    md += `- Target: ${latestIntake.targetOutput}\n`;
    md += `- Stack: ${latestIntake.stackPreference}\n\n`;
  }
  if (latestPlan) {
    md += `## Artifact Plan\n`;
    md += `Deploy: ${latestPlan.deployTarget}\n\n`;
    md += `### Commands\n${latestPlan.commands.map((c) => `- \`${c}\``).join("\n")}\n\n`;
    md += `### Verification\n${latestPlan.verification.map((v) => `- [ ] ${v}`).join("\n")}\n`;
  }
  return md;
}

export function exportRepoAuditJson(result?: RepoGraderResult): string {
  return JSON.stringify(result ?? { error: "No grader result yet" }, null, 2);
}

export function exportHandoffPrompt(prompt?: PromptForgeResult): string {
  return prompt?.markdown ?? "# No prompt forged yet\n\nUse Prompt Forge to generate a handoff.";
}

export function exportMergePlanJson(plan?: MergePlan): string {
  return JSON.stringify(plan ?? { error: "No merge plan yet" }, null, 2);
}

export function exportFullStateJson(state: BobState): string {
  return JSON.stringify(
    {
      version: "1.0.0",
      exportedAt: new Date().toISOString(),
      intakes: state.intakes,
      graderResults: state.graderResults,
      prompts: state.prompts,
      artifactPlans: state.artifactPlans,
      mergePlans: state.mergePlans,
      sovereign: state.sovereign,
    },
    null,
    2,
  );
}

export function planToMarkdown(plan: ArtifactPlan): string {
  return `# Artifact Plan — ${plan.id}

Deploy target: ${plan.deployTarget}

## File tree
${plan.fileTree.map((n) => `- ${n.kind === "dir" ? "📁" : "📄"} ${n.path}${n.note ? ` — ${n.note}` : ""}`).join("\n")}

## Required files
${plan.requiredFiles.map((f) => `- ${f}`).join("\n")}

## Missing (to create)
${plan.missingFiles.map((f) => `- ${f}`).join("\n")}

## Commands
${plan.commands.map((c) => `- \`${c}\``).join("\n")}

## Verification
${plan.verification.map((v) => `- [ ] ${v}`).join("\n")}
`;
}
